/**
 * THIS FILE CONTAINS DEFINITIONS FOR STATIC METRICS
 */

import { tScoreCategory } from 'lib/documents';
import {
  RequiredVisualisationData,
  StaticBrainFunctionVisualisation,
  SymptomCardDataGenerator
} from './types';
import { impactCurry, promisCurry, tryGetAtKey } from './utils';

/**
 * Use `getStaticVisualisation` to safely get visualisations
 */
export const STATIC_VISUALISATIONS: {
  [name: string]: StaticBrainFunctionVisualisation;
} = {} as const;

/**
 * WARN: Breaks typescript build-time checks
 * @param name
 * @param visualisation
 */
function addStaticVisualisation(
  name: string,
  visualisation: StaticBrainFunctionVisualisation
) {
  if (name in STATIC_VISUALISATIONS) {
    throw new Error(
      `[addVisualisation] '${name}' is already associated with a StaticBrainFunctionVisualisation`
    );
  }

  STATIC_VISUALISATIONS[name] = visualisation;
}

export function getStaticVisualisation(
  name: string,
  override?: Partial<StaticBrainFunctionVisualisation>
) {
  if (!(name in STATIC_VISUALISATIONS)) {
    throw new Error(
      `[getStaticVisualisation] Unmapped visualisation '${name}'`
    );
  }
  return { ...STATIC_VISUALISATIONS[name], ...override };
}

///////////////////////// ADD STATIC METRIC DEFINITIONS /////////////////////

addStaticVisualisation('rt', {
  type: 'static',
  label: 'Reaction Time',
  /**
   * ImPact raw score
   */
  getValues: impactCurry('userReactionTimeCompositeScore'),
  scale: [0, 0.6, 0.7, 1],
  isRawScore: true
});

const vmpsFn: SymptomCardDataGenerator = (
  ..._args: [RequiredVisualisationData]
) =>
  /* 2024-11-25 Visual Motor Processing Speed should be rounded to the nearest number with one decimal */
  impactCurry('userVisualMotorCompositeScore')(..._args).map(val => ({
    ...val,
    value: Number(val.value.toFixed(1))
  }));
vmpsFn.source = impactCurry('userVisualMotorCompositeScore').source;

addStaticVisualisation('vmps', {
  type: 'static',
  label: 'Visual Motor Processing Speed',
  /**
   * ImPact raw score
   */
  getValues: vmpsFn,
  scale: [55, 42, 38, 2],
  isRawScore: true
});

const vestibularBalanceFn: SymptomCardDataGenerator = args =>
  args.dhi.map((val) => ({
    date: val.date,
    value: val.data['Total'],
    assessmentType: tryGetAtKey(args.assessmentIndexes, val.id).assessmentType
  }));

vestibularBalanceFn.source = ['dhi'];
addStaticVisualisation('vestibular/balance', {
  type: 'static',
  label: 'Vestibular/Balance',
  /**
   * Total score of all the DHI questions
   * There are 25 questions and the scale is 0-2.
   * The total is multiplied by 2 to bring the total to 100
   */
  getValues: vestibularBalanceFn,
  scale: [0, 15, 25, 100],
  isRawScore: true
});

addStaticVisualisation('physical-function', {
  type: 'static',
  label: 'Physical Function',
  /**
   * T-score of the physical function questions in PROMIS - the first 8 questions
   * The physical mobility section includes "physical function" and "health"
   */
  getValues: promisCurry(
    ['Physical Function'],
    tScoreCategory.PhysicalFunction
  ),
  scale: [100, 60, 40, 0]
});

addStaticVisualisation('pain', {
  type: 'static',
  label: 'Pain',
  /**
   * T-score of the pain questions in PROMIS - 7th set of question (9 questions)
   */
  getValues: promisCurry(
    ['Pain Interferance'],
    tScoreCategory.PainInterferance
  ),
  scale: [0, 55, 60, 70],
});

addStaticVisualisation('sleep', {
  type: 'static',
  label: 'Sleep',
  /**
   * T-score of the sleep disturbance questions in PROMIS - 5th set of question (8 questions)
   */
  getValues: promisCurry(
    ['Sleep Disturbance'],
    tScoreCategory.SleepDisturbance
  ),
  scale: [0, 40, 60, 100]
});

addStaticVisualisation('attention/memory', {
  type: 'static',
  label: 'Attention/Memory',
  /**
   * ImPact raw score
   */
  getValues: impactCurry('userMemoryCompositeScoreVerbal'),
  scale: [100, 95, 85, 0],
  isRawScore: true
});

addStaticVisualisation('cognitive-function', {
  type: 'static',
  label: 'Cognitive Function',
  /**
   * T-score of the cognitive function abilities questions in PROMIS - 9th set of question (8 questions)
   */
  getValues: promisCurry(
    [
      'Cognitive Function Abilities',
      /**
       * Temporarily include the `Cognitive Func. Ab.` key for legacy patients
       *
       * https://trello.com/c/O88YnB44
       */
      'Cognitive Func. Ab.' as any
    ],
    tScoreCategory.CognitiveFunctionAbilities
  ),
  scale: [100, 60, 40, 0]
});

addStaticVisualisation('fatigue', {
  type: 'static',
  label: 'Fatigue',
  /**
   * T-score of the fatigue questions in PROMIS - 4th set of question (8 questions)
   */
  getValues: promisCurry(['Fatigue'], tScoreCategory.Fatigue),
  scale: [0, 40, 60, 100]
});

addStaticVisualisation('anxiety', {
  type: 'static',
  label: 'Anxiety',
  /**
   * T-score of the anxiety questions in PROMIS - 2nd set of question (8 questions)
   */
  getValues: promisCurry(['Anxiety'], tScoreCategory.Anxiety),
  scale: [0, 40, 60, 100]
});

addStaticVisualisation('depression', {
  type: 'static',
  label: 'Depression',
  /**
   * T-score of the depression questions in PROMIS - 3rd set of question (8 questions)
   */
  getValues: promisCurry(['Depression'], tScoreCategory.Depression),
  scale: [0, 40, 60, 100]
});

addStaticVisualisation('social-function', {
  type: 'static',
  label: 'Social Function',
  /**
   * T-score of the Ability to Participate in Social Roles and Activities questions in PROMIS - 6th set of question (8 questions)
   */
  getValues: promisCurry(
    ['Abilities to participate in social roles and activities'],
    tScoreCategory.AbilityToParticipateInSocialRolesAndActivities
  ),
  scale: [100, 60, 40, 0]
});

export function serialiseStaticDialConfig() {
  return Object.values(STATIC_VISUALISATIONS).map(_ => ({
    label: _.label,
    scale: _.scale,
    source: _.getValues.source
  }));
}

// console.debug(JSON.stringify(serialiseStaticDialConfig()))
